import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { Checkbox, Icon, Message, Popup } from 'semantic-ui-react';

import * as templateHelper from '../../../../components/template/templateHelper';
import {
  CharityCountry,
  ProgramFeature,
  getCountryLabel,
  getCountryOptions,
  getFeatureEnabledCount,
  getRemovedCountriesWithCharities,
  isCountryAllowed,
  normaliseCountry,
  programFeature,
  toggleCountry
} from '../../helpers/featureCountries';

import styles from './templateFeatureCountries.module.css';

const i18nBase = 'programs.template.page.setup.feature-countries';

interface TemplateFeatureCountriesProps {
  id: string;
  /** Template path holding the feature's list of country codes. */
  path: string;
  /** Template path holding the program's charity countries. */
  charityCountriesPath: string;
  feature: ProgramFeature;
  /** The list as last saved, to warn before charities lose the feature. */
  savedCountries?: string[] | null;
  label: string;
  onChange: (path: string, value: unknown) => void;
}

export const TemplateFeatureCountries: React.FC<
  TemplateFeatureCountriesProps
> = ({
  id,
  path,
  charityCountriesPath,
  feature,
  savedCountries,
  label,
  onChange
}) => {
  const selected = useSelector((state: any) =>
    templateHelper.getValue(state.templateBuilderEx, path)
  );
  const charityCountries: CharityCountry[] | undefined = useSelector(
    (state: any) =>
      templateHelper.getValue(state.templateBuilderEx, charityCountriesPath)
  );

  const options = useMemo(
    () => getCountryOptions(charityCountries, selected, savedCountries),
    [charityCountries, selected, savedCountries]
  );
  const selectedCodes = useMemo(
    () =>
      (Array.isArray(selected) ? selected : [])
        .map((country: string) => normaliseCountry(country))
        .filter(Boolean),
    [selected]
  );
  const removed = getRemovedCountriesWithCharities(
    savedCountries,
    selected,
    charityCountries,
    feature
  );
  const featureName = I18n.t(
    feature === programFeature.fundraisingGuarantee
      ? `${i18nBase}.fundraising-guarantee`
      : `${i18nBase}.registration-fee`
  );
  const labelId = `${id}-label`;

  return (
    <div className="field" id={id}>
      <label id={labelId}>{I18n.t(label)}</label>
      {options.length === 0 ? (
        <Message
          info
          size="small"
          content={I18n.t(`${i18nBase}.no-charities`)}
        />
      ) : (
        <div role="group" aria-labelledby={labelId} className={styles.list}>
          {options.map((option) => {
            const allowed = isCountryAllowed(feature, option.country);
            const countryLabel = getCountryLabel(option.country);
            return (
              <div key={option.country} className={styles.row}>
                <Checkbox
                  id={`${id}-${option.country}`}
                  label={countryLabel}
                  checked={selectedCodes.includes(option.country)}
                  disabled={!allowed}
                  onChange={(_, data) =>
                    onChange(
                      path,
                      toggleCountry(selected, option.country, !!data.checked)
                    )
                  }
                />
                {!allowed && (
                  <Popup
                    on={['hover', 'focus']}
                    content={I18n.t(`${i18nBase}.guarantee-not-allowed`)}
                    trigger={
                      <Icon
                        name="info circle"
                        tabIndex={0}
                        role="img"
                        aria-label={I18n.t(`${i18nBase}.guarantee-not-allowed`)}
                        className={styles.info}
                      />
                    }
                  />
                )}
                <span className={styles.counts}>
                  {I18n.t(`${i18nBase}.charity-counts`, {
                    count: option.charityCount,
                    enabled: getFeatureEnabledCount(option, feature)
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div className="input-sub input-sub-bottom">
        {I18n.t(`${i18nBase}.subtext`, { feature: featureName })}
      </div>
      {removed.length > 0 && (
        <Message
          negative
          role="alert"
          size="small"
          list={removed.map((item) =>
            I18n.t(`${i18nBase}.removal-warning`, {
              country: getCountryLabel(item.country),
              feature: featureName,
              count: item.count
            })
          )}
        />
      )}
    </div>
  );
};

export default TemplateFeatureCountries;
